import React, { useEffect, useRef, useState } from 'react';
import { ModuleBase, Stack, Text, Link, Box, ResponsiveImage } from '@/components';
import { SideBySideModuleCard, SideBySideModuleModel } from './SideBySideModule';
import classNames from 'classnames';
import Flicking from '@egjs/react-flicking';
import tailwindConfig from '@/tailwind.screens';
import { fadeIn } from '@/animations';
import { motion } from 'framer-motion';
import Collapse from '@/assets/collapse.svg';
import Expand from '@/assets/expand.svg';
import DropdownArrow from '@/assets/caretDownLight.svg';

const MembershipTrusted = (data: SideBySideModuleModel) => {
  const {
    props: { preTitle, title, cards, link1, link2 },
  } = data;

  const [expandedIndex, setExpandedIndex] = useState(-1);
  const [holding, setHolding] = useState(false);
  const backgroundColour = '#F9F2E1';

  const carousel = useRef<Flicking>(null);
  const [disabledNext, setDisabledNext] = useState(false);
  const [disabledPrev, setDisabledPrev] = useState(true);

  const _setExpandedIndex = (index: number) => {
    if (expandedIndex === index) {
      setExpandedIndex(-1);
    } else {
      setExpandedIndex(index);
    }
  };

  const onCarouselChanged = () => {
    if (carousel.current) {
      if (carousel.current.index === 0) {
        setDisabledPrev(true);
      } else {
        setDisabledPrev(false);
      }
      if (carousel.current.index === (cards ? cards.length - 1 : 0)) {
        setDisabledNext(true);
      } else {
        setDisabledNext(false);
      }
    }
  };

  const next = () => {
    if (carousel.current && !carousel.current.animating) {
      carousel.current.next().catch(() => void 0);
    }
  };

  const prev = () => {
    if (carousel.current && !carousel.current.animating) {
      carousel.current.prev().catch(() => void 0);
    }
  };

  return (
    <ModuleBase
      data={{ ...data, props: { ...data.props, backgroundColour } }}
      noPadding
      className="MembershipTrusted overflow-hidden py-10 lg:py-[50px]"
    >
      <Stack className="container flex flex-col justify-between lg:flex-row lg:items-stretch">
        <div className="relative z-10 flex min-h-full lg:w-[430px] lg:pr-4">
          <div
            className="absolute top-0 right-0 flex h-full w-dvw flex-col justify-between"
            style={{ background: backgroundColour || 'white' }}
          />

          <div className="color-from-bg relative flex w-full flex-col lg:py-[22px]">
            <div className="flex flex-grow flex-col gap-3">
              <Text className="text-style-tag uppercase">{preTitle}</Text>
              {title && <Text className="text-style-h3 xl:w-4/5" dangerouslySetInnerHTML={{ __html: title }} />}
            </div>
            {(link1 || link2) && (
              <div className="mt-10 flex gap-5">
                {link1 && <Link animate className="btn primary v2 !text-[#F9F2E1] hover:!text-black" link={link1} />}
                {link2 && <Link animate className="btn secondary v2" link={link2} />}
              </div>
            )}
            <Box animation={fadeIn} className="hidden justify-end gap-3 lg:mr-5 lg:flex">
              <button
                className="cursor-pointer px-2 disabled:cursor-default disabled:opacity-50"
                onClick={prev}
                disabled={disabledPrev}
              >
                <DropdownArrow className="fill-from-bg rotate-90" />
              </button>
              <button
                className="cursor-pointer px-2 disabled:cursor-default disabled:opacity-50"
                onClick={next}
                disabled={disabledNext}
              >
                <DropdownArrow className="fill-from-bg -rotate-90" />
              </button>
            </Box>
          </div>
        </div>
        <div className="relative mt-10 lg:mt-0 lg:w-[calc(100%-430px)]">
          {cards && (
            <Flicking
              ref={carousel}
              className={classNames('!overflow-visible lg:-mx-1.5', holding ? 'cursor-grabbing' : 'cursor-grab')}
              preventClickOnDrag
              preventDefaultOnDrag
              align="prev"
              bound
              resizeOnContentsReady
              onHoldStart={() => setHolding(true)}
              onHoldEnd={() => setHolding(false)}
              onChanged={onCarouselChanged}
            >
              {cards.map((card, index) => (
                <div
                  key={index}
                  className="card xxl:w-[298px] flex w-[247px] flex-col px-2.5 first:pl-0 last:pr-0 md:w-[290px] first:lg:pl-2.5 xl:w-[290px]"
                >
                  <Box animation={fadeIn}>
                    <CaseStudyCard
                      card={card}
                      expanded={expandedIndex === index}
                      onExpand={() => _setExpandedIndex(index)}
                    />
                  </Box>
                </div>
              ))}
            </Flicking>
          )}
        </div>
      </Stack>
    </ModuleBase>
  );
};

export default MembershipTrusted;

const transition = {
  type: 'spring',
  damping: 20,
  stiffness: 95,
};

const fadeUp = (marginTop: string, y?: number) => {
  return {
    cardInactive: {
      opacity: 0,
      marginTop: 0,
      y: y || 0,
    },
    cardActive: {
      opacity: 1,
      marginTop: marginTop,
      y: 0,
      transition: {
        height: {
          ...transition,
        },
        opacity: {
          type: 'spring',
          delay: 0.3,
          damping: 30,
          stiffness: 95,
        },
        y: {
          ...transition,
        },
      },
    },
  };
};

const containerAnims = (containerHeightInactive: number, containerHeightActive: number) => {
  return {
    variants: {
      cardInactive: {
        height: containerHeightInactive,
        transition,
      },
      cardActive: {
        height: containerHeightActive,
        transition,
      },
    },
  };
};

const childAnims = {
  image: {
    variants: {
      cardInactive: {
        opacity: 100,
        transition,
      },
      cardActive: {
        opacity: 0,
        transition,
      },
    },
  },
  description: {
    variants: fadeUp('20px'),
  },
  buttonGroup: {
    variants: fadeUp('10px', 50),
  },
};

const CaseStudyCard = ({
  card,
  expanded,
  onExpand,
}: {
  card: SideBySideModuleCard;
  expanded: boolean;
  onExpand: () => void;
}) => {
  const [containerHeightInactive, setContainerHeightInactive] = useState(240);
  const [containerHeightActive, setContainerHeightActive] = useState(400);
  useEffect(() => {
    const onResize = () => {
      setContainerHeightInactive(
        window.innerWidth >= parseInt(tailwindConfig.screens.lg, 10)
          ? 260
          : window.innerWidth >= parseInt(tailwindConfig.screens.md, 10)
            ? 200
            : 180,
      );
      setContainerHeightActive(
        window.innerWidth >= parseInt(tailwindConfig.screens.lg, 10)
          ? 420
          : window.innerWidth >= parseInt(tailwindConfig.screens.md, 10)
            ? 360
            : 340,
      );
    };

    onResize();

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <motion.div initial="cardInactive" animate={expanded ? 'cardActive' : 'cardInactive'} className="relative">
      <div className="h-[160px] w-full">
        {card.image && (
          <ResponsiveImage animation={childAnims.image} image={card.image} widths={{ xs: 290 }} heights={{ xs: 188 }} />
        )}
      </div>
      <div className="relative h-[180px] md:h-[200px] lg:h-[260px]" />
      <Box
        animation={containerAnims(containerHeightInactive, containerHeightActive)}
        className="xxl:p-5 absolute bottom-0 flex w-full flex-col justify-between overflow-hidden bg-white p-3 lg:p-4"
      >
        <div>
          {card.preHeading && <div className="text-style-tag mb-3 leading-5 text-[#AE2D2A]">{card.preHeading}</div>}
          <p className="text-style-h5 line-clamp-3">{card.title}</p>
          {card.text && (
            <Text
              animation={childAnims.description}
              dangerouslySetInnerHTML={{ __html: card.text }}
              className="text-style-body"
            />
          )}
        </div>

        {card.link && (
          <Box animation={childAnims.buttonGroup} className="flex justify-end">
            <Link link={card.link} className="btn !text-style-body v2 whitespace-nowrap underline">
              <span className="whitespace-nowrap">{card.link.text}</span>
            </Link>
          </Box>
        )}
      </Box>

      <div className="xxl:bottom-5 xxl:left-5 absolute bottom-3 left-3">
        <button className="btn icon secondary !border-black" onClick={onExpand}>
          {expanded ? <Collapse className="stroke-black" /> : <Expand className="stroke-black" />}
        </button>
      </div>
    </motion.div>
  );
};
